/**
 * Use this hook to check if user has permission to perform an action that is
 * restricted to users with specific subscription plans.
 */
import useAuthActiveSubscriptionTiers from '@apps/www/src/www/hooks/useAuthActiveSubscriptionTiers';
import useAuthUser from '@apps/www/src/www/hooks/useAuthUser';
import useIsLoggedIn from '@apps/www/src/www/hooks/useIsLoggedIn';
import useUIState, { UIStateKeys } from '@apps/www/src/www/hooks/useUIState';
import config from '@pkgs/shared-client/config';
import AllowedFeature from '@pkgs/shared/enums/AllowedFeature';
import SubscriptionTier from '@pkgs/shared/enums/SubscriptionTier';
import hasAllowedFeatureFromSubscriptionTiers from '@pkgs/shared/helpers/hasAllowedFeatureFromSubscriptionTiers';
import { isAllowedToAddBoard, isAllowedToAddItem } from '@pkgs/shared/plans';

function isAllowed(
	allowedFeature: ValueOf<typeof AllowedFeature>,
	info: {
		tiers: Array<ValueOf<typeof SubscriptionTier>>;
		itemsCount: number;
		boardsCount: number;
		showGridControls: boolean;
	},
) {
	if (allowedFeature === AllowedFeature.ADD_ITEM) {
		return isAllowedToAddItem(config, info.tiers, info.itemsCount);
	} else if (allowedFeature === AllowedFeature.ADD_BOARD) {
		return isAllowedToAddBoard(config, info.tiers, info.boardsCount);
	} else if (allowedFeature === AllowedFeature.RIGHT_CLICK && !info.showGridControls) {
		return true;
	}

	return hasAllowedFeatureFromSubscriptionTiers(info.tiers, allowedFeature);
}

export enum ALLOWED_STATES {
	ALLOWED = 'ALLOWED',
	NOT_ALLOWED = 'NOT_ALLOWED',
	LOGGED_OUT = 'LOGGED_OUT',
}

export default function useAllowedFeatureState(
	allowedFeature: ValueOf<typeof AllowedFeature> | null | undefined,
) {
	const isLoggedIn = useIsLoggedIn();
	const authUser = useAuthUser(['itemsCount', 'boardsCount']);
	const tiers = useAuthActiveSubscriptionTiers();
	const [showGridControls] = useUIState(UIStateKeys.SHOW_GRID_CONTROLS);

	if (allowedFeature) {
		if (!isLoggedIn || !authUser) {
			return ALLOWED_STATES.LOGGED_OUT;
		} else if (
			!isAllowed(allowedFeature, {
				tiers,
				itemsCount: authUser.itemsCount || 0,
				boardsCount: authUser.boardsCount || 0,
				showGridControls,
			})
		) {
			return ALLOWED_STATES.NOT_ALLOWED;
		}
	}

	return ALLOWED_STATES.ALLOWED;
}
